import { useState, useEffect } from 'react';

function TradingChartModal({ isOpen, onClose, instrument }) {
  const [chartData, setChartData] = useState([]);
  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState({});

  useEffect(() => {
    if (isOpen && instrument) {
      generateChartData();
    }
  }, [isOpen, instrument]);

  const generateChartData = () => {
    if (!instrument) return;

    // Base prices for each instrument
    const basePrices = {
      XAUUSD: 2635,
      NAS100: 16245,
      GBPJPY: 193.56,
      SP500: 4567
    };

    const basePrice = basePrices[instrument.symbol] || 100;
    const volatility = {
      XAUUSD: 15,
      NAS100: 80,
      GBPJPY: 0.8,
      SP500: 30
    }[instrument.symbol] || 5;

    // Generate 60 data points (representing 1 hour of trading)
    const data = [];
    let currentPrice = basePrice;

    for (let i = 0; i < 60; i++) {
      const change = (Math.random() - 0.5) * volatility;
      currentPrice += change;
      
      data.push({
        time: i,
        price: parseFloat(currentPrice.toFixed(2)),
        high: parseFloat((currentPrice + Math.random() * volatility * 0.5).toFixed(2)),
        low: parseFloat((currentPrice - Math.random() * volatility * 0.5).toFixed(2)),
        open: parseFloat(currentPrice.toFixed(2)),
        close: parseFloat((currentPrice + change).toFixed(2))
      });
    }

    setChartData(data);

    // Generate sample trade scenarios (illustrative only)
    const aiTrades = [
      {
        entry: 8,
        exit: 15,
        type: 'buy',
        profit: Math.floor(Math.random() * 3000 + 1500),
        pips: (Math.random() * 50 + 20).toFixed(1)
      },
      {
        entry: 20,
        exit: 28,
        type: 'sell',
        profit: Math.floor(Math.random() * 2500 + 1200),
        pips: (Math.random() * 45 + 18).toFixed(1)
      },
      {
        entry: 35,
        exit: 42,
        type: 'buy',
        profit: Math.floor(Math.random() * 3500 + 2000),
        pips: (Math.random() * 55 + 25).toFixed(1)
      },
      {
        entry: 48,
        exit: 55,
        type: 'buy',
        profit: Math.floor(Math.random() * 2800 + 1400),
        pips: (Math.random() * 48 + 22).toFixed(1)
      }
    ];

    setTrades(aiTrades);

    // Calculate stats (illustrative demonstration only)
    const totalProfit = aiTrades.reduce((sum, trade) => sum + trade.profit, 0);

    setStats({
      totalProfit,
      totalTrades: aiTrades.length,
      avgProfit: Math.floor(totalProfit / aiTrades.length),
      timeframe: '1 Hour Demo',
      disclaimer: 'Illustrative only'
    });
  };

  if (!isOpen || !instrument) return null;

  const maxPrice = Math.max(...chartData.map(d => d.high));
  const minPrice = Math.min(...chartData.map(d => d.low));
  const priceRange = maxPrice - minPrice;

  // SVG dimensions
  const width = 800;
  const height = 400;
  const padding = 40;

  const xScale = (time) => padding + (time / (chartData.length - 1)) * (width - 2 * padding);
  const yScale = (price) => height - padding - ((price - minPrice) / priceRange) * (height - 2 * padding);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-sm px-4" onClick={onClose}>
      <div className="relative w-full max-w-6xl bg-zinc-950 rounded-2xl shadow-2xl border border-zinc-800 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 hover:bg-zinc-800 rounded-full transition-colors"
          aria-label="Close"
        >
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-zinc-900 to-black p-6 border-b border-zinc-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-4xl">{instrument.icon}</span>
              <div>
                <h3 className="text-2xl font-light text-white mb-1">{instrument.name}</h3>
                <p className="text-sm text-gray-400">Kasango AI Pro Live Demo</p>
              </div>
            </div>
            <div className="text-right">
              <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-500/50 px-4 py-2 rounded-full">
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                <span className="text-blue-400 font-bold text-sm">Demo Mode</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-zinc-900/50 border-b border-zinc-800">
          <div className="text-center">
            <div className="text-2xl font-bold text-[#00FF7F]">+R{stats.totalProfit?.toLocaleString()}</div>
            <div className="text-xs text-gray-400">Total Profit</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-white">{stats.totalTrades}</div>
            <div className="text-xs text-gray-400">AI Trades</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-white">R{stats.avgProfit?.toLocaleString()}</div>
            <div className="text-xs text-gray-400">Avg Profit/Trade</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-400">{stats.timeframe}</div>
            <div className="text-xs text-gray-400">Timeframe</div>
          </div>
        </div>

        {/* Chart */}
        <div className="p-6 bg-black">
          <div className="bg-zinc-950 rounded-xl p-4 border border-zinc-800">
            <svg width="100%" height="450" viewBox={`0 0 ${width} ${height + 50}`} className="overflow-visible">
              {/* Grid Lines */}
              {[0, 1, 2, 3, 4].map((i) => {
                const y = padding + (i * (height - 2 * padding)) / 4;
                const price = maxPrice - (i * priceRange) / 4;
                return (
                  <g key={`grid-${i}`}>
                    <line
                      x1={padding}
                      y1={y}
                      x2={width - padding}
                      y2={y}
                      stroke="#27272a"
                      strokeWidth="1"
                      strokeDasharray="4"
                    />
                    <text x={padding - 10} y={y + 4} textAnchor="end" fill="#71717a" fontSize="12">
                      {price.toFixed(2)}
                    </text>
                  </g>
                );
              })}

              {/* Price Line */}
              <path
                d={chartData.map((d, i) => 
                  `${i === 0 ? 'M' : 'L'} ${xScale(d.time)} ${yScale(d.price)}`
                ).join(' ')}
                fill="none"
                stroke="#3b82f6"
                strokeWidth="2"
              />

              {/* AI Trade Markers */}
              {trades.map((trade, idx) => {
                const entryPoint = chartData[trade.entry];
                const exitPoint = chartData[trade.exit];
                if (!entryPoint || !exitPoint) return null;

                const entryX = xScale(entryPoint.time);
                const entryY = yScale(entryPoint.price);
                const exitX = xScale(exitPoint.time);
                const exitY = yScale(exitPoint.price);

                return (
                  <g key={`trade-${idx}`}>
                    {/* Trade Line */}
                    <line
                      x1={entryX}
                      y1={entryY}
                      x2={exitX}
                      y2={exitY}
                      stroke={trade.type === 'buy' ? '#00FF7F' : '#ef4444'}
                      strokeWidth="3"
                      strokeDasharray="5,5"
                      opacity="0.6"
                    />

                    {/* Entry Marker */}
                    <circle
                      cx={entryX}
                      cy={entryY}
                      r="6"
                      fill={trade.type === 'buy' ? '#00FF7F' : '#ef4444'}
                      stroke="white"
                      strokeWidth="2"
                    />
                    <text
                      x={entryX}
                      y={entryY - 15}
                      textAnchor="middle"
                      fill="#00FF7F"
                      fontSize="11"
                      fontWeight="bold"
                    >
                      {trade.type === 'buy' ? '↑ BUY' : '↓ SELL'}
                    </text>

                    {/* Exit Marker with Profit */}
                    <circle
                      cx={exitX}
                      cy={exitY}
                      r="6"
                      fill="#fbbf24"
                      stroke="white"
                      strokeWidth="2"
                    />
                    <rect
                      x={exitX - 40}
                      y={exitY - 40}
                      width="80"
                      height="24"
                      fill="#00FF7F"
                      rx="4"
                    />
                    <text
                      x={exitX}
                      y={exitY - 24}
                      textAnchor="middle"
                      fill="black"
                      fontSize="12"
                      fontWeight="bold"
                    >
                      +R{trade.profit}
                    </text>
                  </g>
                );
              })}

              {/* X-axis labels */}
              {[0, 15, 30, 45, 60].map((time) => (
                <text
                  key={`x-${time}`}
                  x={xScale(time)}
                  y={height - padding + 20}
                  textAnchor="middle"
                  fill="#71717a"
                  fontSize="12"
                >
                  {time}m
                </text>
              ))}
            </svg>

            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-4 text-xs text-gray-400">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#00FF7F]"></div>
                <span>AI Entry</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#fbbf24]"></div>
                <span>Exit with Profit</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-0.5 bg-blue-500"></div>
                <span>Price Movement</span>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-zinc-800 to-zinc-900 p-6 border-t border-zinc-700">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <h4 className="text-xl font-bold text-white mb-1">
                🤖 Kasango AI Pro – Trading Assistance Software
              </h4>
              <p className="text-gray-400 text-sm">
                Automated Execution • Rule-Based Logic • R1,450 One-Time Payment
              </p>
              <p className="text-xs text-gray-500 mt-2">
                ⚠️ Trading involves risk. Results vary. No guaranteed returns.
              </p>
            </div>
            <div className="flex gap-3">
              <a
                href="https://whop.com/checkout/plan_2ekgQsmNUX0es?d2c=true"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-[#00FF7F] hover:bg-green-500 text-black font-bold rounded-xl transition-all hover:scale-105 shadow-lg"
              >
                Get Kasango AI Pro →
              </a>
              <a
                href="https://wa.me/27685358462?text=Hi%20John%2C%20I%20want%20to%20learn%20more%20about%20Kasango%20AI%20Pro!"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-zinc-700 hover:bg-zinc-600 text-white font-bold rounded-xl transition-all hover:scale-105 shadow-lg"
              >
                Learn More
              </a>
            </div>
          </div>
        </div>

        {/* Legal Disclaimer */}
        <div className="bg-black p-4 border-t border-zinc-800">
          <p className="text-xs text-gray-500 text-center leading-relaxed">
            <span className="font-semibold text-gray-400">Demonstration Only:</span> Charts show illustrative scenarios, not actual results. Trading involves significant risk of loss. Kasango AI Pro is a software tool, not a financial service or investment product. Past or simulated performance does not guarantee future results. Results vary by user.
          </p>
        </div>
      </div>
    </div>
  );
}

export default TradingChartModal;
